/**
 * Logo — the Timlul brand logo system. The single source for the mark + wordmark.
 *
 * The mark is the app's brand icon (serif "T" + audio-waveform on the teal
 * gradient), rendered from the existing /favicon.svg so there is exactly one
 * copy of the mark geometry in the app. It is self-contained (own gradient +
 * rounded tile) and reads correctly on light and dark with no per-theme work.
 *
 * Usage:
 *   <Logo />                            full bilingual lockup, default (md) size
 *   <Logo variant="mark" size="sm" />   mark only (collapsed rail / tight spots)
 *   <Logo size="lg" stacked />          large centered lockup (login hero)
 *
 * Note: `stacked` is ignored for variant="mark" (no wordmark to stack).
 */

export type LogoVariant = "lockup" | "mark";
export type LogoSize = "sm" | "md" | "lg";

interface LogoProps {
  /** "lockup" = mark + wordmark (default); "mark" = mark only. */
  variant?: LogoVariant;
  /** sm = collapsed rail; md = sidebar/mobile; lg = login hero. Default md. */
  size?: LogoSize;
  /** Stack the wordmark below the mark, centered (login hero). Default inline. */
  stacked?: boolean;
  className?: string;
}

export function Logo({
  variant = "lockup",
  size = "md",
  stacked = false,
  className,
}: LogoProps) {
  const markOnly = variant === "mark";
  const classes = ["logo", `logo--${size}`, stacked && !markOnly ? "logo--stacked" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes}>
      <img
        className="logo-mark"
        src="/favicon.svg"
        alt={markOnly ? "Timlul" : ""}
        draggable={false}
      />
      {!markOnly && (
        <span className="logo-wordmark">
          <span className="logo-en">Timlul</span>
          <span className="logo-he">· תמלול</span>
        </span>
      )}
    </span>
  );
}
